import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {ForEachState} from 'src/reducers/for-each';
import type {GenericObject} from 'src/types/sense';
import type {ForEachAPI} from 'src/types/for-each';
import {RRule} from 'rrule';
import type {
  ForEachHelperObject,
  NodeOutputMap,
  NodeOutput,
} from 'src/components/automation/for-each/usePreviousNodesMatch';
import type {
  DynamicTaskObjectModules,
  DynamicTaskObject,
} from 'src/components/lib/automation-module-registry/automation-module-registry';
import type {TranslationFunction} from 'src/types/language-support';
import {
  extractLastParamFromJSONPath,
  getVariableIdentifiers,
  skipFieldsRecursively,
} from 'src/components/lib/automation-module-registry/utils';

import {formatPlural} from 'src/utils/intl';


export type Validations = {
  forEachMatchEmpty: boolean;
  outputEmpty: boolean;
  intervalInvalid: boolean;
  batchInvalid: boolean;
};

export const BATCH_SIZE_OPTIONS = [
  {key: '25', label: '25'},
  {key: '50', label: '50'},
  {key: '75', label: '75'},
  {key: '100', label: '100'},
] as const;

const BATCH_FREQUENCIES = {
  [RRule.HOURLY]: 'hour',
  [RRule.DAILY]: 'day',
} as const;

export const getDefaultForEachState = (): ForEachState => {
  return {
    batchSize: '25',
    previousNodeId: null,
    idKey: null,
    // @ts-expect-error - TS2322 - Type 'import("/Users/diwaker/SpacedOut/ui-server/node_modules/rrule/dist/esm/types").Frequency' is not assignable to type 'string'.
    frequency: RRule.DAILY,
    interval: '1',
    batching: false,
  };
};

export const getForEachOptions = (
  nodeOutputMap: NodeOutputMap,
): Array<MenuOption> => {
  const options: Array<MenuOption> = [];
  nodeOutputMap.forEach((value, key) => {
    options.push({key, label: value.title});
  });
  return options;
};

export const getOutputOptions = (
  nodeOutputMap: NodeOutputMap,
  previousNodeId?: string | null,
): Array<MenuOption> => {
  const options: Array<MenuOption> = [];
  if (!previousNodeId) {
    return options;
  }
  const completeDataObject = nodeOutputMap.get(previousNodeId);
  const outputOptionsArray = completeDataObject
    ? completeDataObject.options
    : [];
  return outputOptionsArray.map(
    ({jsonPath, entityTypeDisplayName, standardEntityType}) => {
      return {
        key: jsonPath,
        label: `${entityTypeDisplayName} (${standardEntityType})`,
      };
    },
  );
};

export const getOutputAndEntityType = (
  nodeOutputMap: NodeOutputMap,
  forEachId?: string | null,
  key?: string | null,
): {
  output: string | null | undefined;
  entityType: string | null | undefined;
} => {
  const result: {
    output: string | null | undefined;
    entityType: string | null | undefined;
  } = {
    output: null,
    entityType: null,
  };
  if (!forEachId || !key) {
    return result;
  }
  const completeDataObject = nodeOutputMap.get(forEachId);
  const outputOptionsArray = completeDataObject
    ? completeDataObject.options
    : [];
  const optionsObject = outputOptionsArray.find(
    ({jsonPath}) => jsonPath === key,
  );
  if (optionsObject) {
    result.output = optionsObject.entityTypeDisplayName;
    result.entityType = optionsObject.standardEntityType;
  }
  return result;
};

export const normalizeForeach = (
  forEachData: ForEachState,
  completeDataObject?: NodeOutput | null,
): ForEachAPI => {
  if (!completeDataObject) {
    throw new Error('Complete data object is required for normalizing foreach');
  }

  const optionsObject = completeDataObject.options.find(
    ({jsonPath}) => jsonPath === forEachData.idKey,
  );
  if (!optionsObject) {
    throw new Error('Options object not found');
  }

  const previousNodeId = forEachData.previousNodeId;
  if (previousNodeId == null) {
    throw new Error('Previous node id is required for normalizing foreach');
  }
  const rruleCount = forEachData.batching
    ? Math.ceil(
        parseInt(completeDataObject.count, 10) /
          // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
          parseInt(forEachData.batchSize, 10),
      )
    : '1';
  const rruleObject = {
    interval: forEachData.interval,
    freq: forEachData.frequency,
    count: rruleCount,
  } as const;
  const batchSize = forEachData.batching
    ? // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
      parseInt(forEachData.batchSize, 10)
    : parseInt(completeDataObject.count, 10);
  // @ts-expect-error - TS2345 - Argument of type '{ readonly interval: string; readonly freq: string; readonly count: number | "1"; }' is not assignable to parameter of type 'Partial<Options>'.
  const rule = RRule.optionsToString(rruleObject);
  return {
    date_type: 'TODAY',
    time_type: 'NOW',
    date_value: 'today',
    time_value: 'now',
    blackout_action: 'DO_NOT_APPLY',
    recurring_rule: {
      rule,
      first_instance: 1,
    },
    frequency_limit_settings: {
      skip: false,
    },
    task: {
      params: {
        [completeDataObject.params.key]: completeDataObject.params.value,
        batch_size: batchSize,
      },
    },
    batch_size: batchSize,
    entity_type: optionsObject.entityType,
    id_key: optionsObject.jsonPath,
    previous_node_id: previousNodeId,
    batching: forEachData.batching,
  };
};

export const parseForeach = (nodeBody: ForEachAPI): ForEachState => {
  const {
    batch_size: batchSize,
    previous_node_id: previousNodeId,
    id_key: idKey,
    recurring_rule: recurringRule,
    batching,
  } = nodeBody;

  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  const rruleObject = RRule.parseString(recurringRule?.rule);
  const {interval, freq} = rruleObject;
  return {
    batchSize: String(batchSize),
    previousNodeId,
    idKey,
    // @ts-expect-error - TS2322 - Type 'import("/Users/diwaker/SpacedOut/ui-server/node_modules/rrule/dist/esm/types").Frequency | undefined' is not assignable to type 'string'.
    frequency: freq,
    // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'string'.
    interval,
    batching,
  };
};
export const getTaskParamsValue = (
  forEachSizeFieldJSONPath: string,
  previousNodeContextKey: string,
  candidateMatchSummaryModuleKey?: string | null,
): string => {
  if (!forEachSizeFieldJSONPath || !previousNodeContextKey) {
    return '';
  }
  const firstIndexOfResponse = forEachSizeFieldJSONPath.indexOf('response');
  const stringToAppend = forEachSizeFieldJSONPath.slice(firstIndexOfResponse);
  return `{{${previousNodeContextKey}${
    candidateMatchSummaryModuleKey ? `.${candidateMatchSummaryModuleKey}` : ''
  }.${stringToAppend}`;
};
const extractCountUsingJSONPath = (
  jsonPathArray: Array<string>,
  matchObject: unknown,
) => {
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
  return jsonPathArray.reduce((acc, key) => acc && acc[key], matchObject);
};
const trimJSONPath = (jsonPath: string): string => {
  const start = jsonPath.indexOf('{{') + 2;
  const end = jsonPath.lastIndexOf('}}');
  return jsonPath.slice(start, end);
};
export const extractCountFromPreviousMatch = (
  previousNodeModules: DynamicTaskObjectModules,
  forEachSizeFieldJSONPath: string,
  taskGroupType: string,
): string => {
  for (const moduleKey of Object.keys(previousNodeModules)) {
    if (previousNodeModules[moduleKey].type === taskGroupType) {
      const matchParams = previousNodeModules[moduleKey].params;
      const forEachJSONPathArray =
        trimJSONPath(forEachSizeFieldJSONPath).split('.') ?? [];
      for (let i = 0; i < forEachJSONPathArray.length; i++) {
        if (matchParams[forEachJSONPathArray[i]]) {
          const count = extractCountUsingJSONPath(
            forEachJSONPathArray.slice(i + 1),
            matchParams[forEachJSONPathArray[i]],
          );
          return String(count);
        }
      }
    }
  }
  return '';
};

export function getForEachHelperObject(
  taskObject: DynamicTaskObject,
  getStaticAutomationModule: (arg1: string) => GenericObject,
): ForEachHelperObject | null | undefined {
  const taskGroupType = taskObject.details.task_group_type;
  if (!taskGroupType) {
    return null;
  }

  const matchedStaticObject = getStaticAutomationModule(taskGroupType);
  if (!matchedStaticObject) {
    return null;
  }

  const dataWithPropertiesSkipped = skipFieldsRecursively(
    {
      [taskGroupType]: matchedStaticObject,
    },
    'properties',
  );

  const jsonPathMap: Map<string, any> = getVariableIdentifiers(
    dataWithPropertiesSkipped,
  );
  const result: ForEachHelperObject = {
    for_each_jump: null,
    for_each_size_field: null,
  };
  jsonPathMap.forEach((pathObject, key) => {
    if (pathObject.for_each_jump) {
      result['for_each_jump'] = {
        jsonPath: key,
        parentPathReference: pathObject.for_each_jump,
      };
    }
    if (pathObject.for_each_size_field === true) {
      result['for_each_size_field'] = {
        jsonPath: key,
        batchKey: extractLastParamFromJSONPath(key),
      };
    }
  });

  return Object.keys(result).length ? result : null;
}

export function getForEachOutputOptions(
  pathReference: string,
  getStaticAutomationModule: (arg1: string) => GenericObject,
): Array<{
  jsonPath: string;
  entityTypeDisplayName: string;
  standardEntityType: string;
  entityType: string;
}> {
  const matchedStaticObject = getStaticAutomationModule(pathReference);
  if (!matchedStaticObject) {
    return [];
  }

  const dataWithPropertiesSkipped = skipFieldsRecursively(
    {
      [pathReference]: matchedStaticObject,
    },
    'properties',
  );

  const jsonPathMap: Map<string, any> = getVariableIdentifiers(
    dataWithPropertiesSkipped,
  );

  return Array.from(jsonPathMap.entries())
    .filter(([, pathObject]: [any, any]) => pathObject.entity_type)
    .map(([key, pathObject]: [any, any]) => ({
      jsonPath: key,
      entityTypeDisplayName: pathObject.entity_type_display_name,
      standardEntityType: pathObject.standard_entity_type,
      entityType: pathObject.entity_type,
    }));
}

export const isIntervalInvalid = (
  isBatchingEnabled: boolean,
  interval: string,
): boolean => {
  if (!isBatchingEnabled) {
    return false;
  }
  const intervalDigit = parseInt(interval, 10);
  // We can only allow whole numbers in the interval between 1 to 10
  if (
    Number.isInteger(intervalDigit) &&
    intervalDigit > 0 &&
    intervalDigit <= 10
  ) {
    return false;
  }
  return true;
};

export const getErrors = (validationsObject: Validations): Array<string> => {
  const errors: Array<string> = [];
  const {forEachMatchEmpty, outputEmpty, intervalInvalid, batchInvalid} =
    validationsObject;
  if (forEachMatchEmpty || outputEmpty || batchInvalid) {
    errors.push('Please fill the required fields.');
  }
  if (intervalInvalid) {
    errors.push('Interval should be a valid integer between 1 and 10');
  }
  return errors;
};

export const getBatchingInfo = ({
  batching,
  batchSize,
  interval,
  frequency,
  t,
}: {
  batching: boolean;
  batchSize: number | string;
  interval: number;
  frequency: number;
  t: TranslationFunction;
}): string => {
  if (!batching) {
    return t('Batching_Off', 'Batching - Off');
  }
  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  const frequencyLabel = BATCH_FREQUENCIES[frequency];
  const mainFrequencyLabel = formatPlural(
    interval,
    frequencyLabel,
    `${frequencyLabel}s`,
  );
  const intervalLabel = interval > 1 ? ` ${interval}` : '';

  return t(`Batching_batchSize_records_every_interval_mainFrequencyLabel`, {
    batchSize,
    interval: intervalLabel,
    mainFrequencyLabel,
  });
};
